import React, { useEffect, useState } from "react";
import {
  Checkbox,
  HStack,
  Icon,
  Input,
  Menu,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  VStack,
  FormControl,
  FormLabel,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FaChevronDown } from "react-icons/fa";
import { useLang } from "../../../../Hooks";
import { toDisplayText } from "../../../../utils/localizedText";
import { ReportActionButton } from "../../Shared/Components/ReportActionButton";
import { ReportFilterButton } from "../../Shared/Components/ReportFilterButton";
import { ReportMenuButton } from "../../Shared/Components/ReportMenuButton";
import { reportToolbarStyles } from "../../Shared/Styles/reportToolbarStyles";
import { CURRENCIES, PERIOD_OPTIONS } from "../../balanceSheetConstants";
import { getEndOfMonth } from "../../balanceSheetUtils";

const MultiSelectMenu = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
}) => (
  <FormControl>
    <FormLabel fontSize="sm">{label}</FormLabel>
    <Menu closeOnSelect={false}>
      <ReportMenuButton w="full">
        <HStack justify="space-between" w="full">
          <Text noOfLines={1}>
            {selectedValues.length === 0
              ? "Select"
              : `${label} (${selectedValues.length})`}
          </Text>
          <Icon as={FaChevronDown} />
        </HStack>
      </ReportMenuButton>
      <MenuList {...reportToolbarStyles.menuList} maxH="220px" overflowY="auto">
        {options.map((option) => {
          const checked = selectedValues.includes(option.id);
          return (
            <MenuItem
              key={option.id}
              onClick={(event) => {
                event.stopPropagation();
                onSelectionChange(
                  checked
                    ? selectedValues.filter((value) => value !== option.id)
                    : [...selectedValues, option.id],
                );
              }}
            >
              <Checkbox isChecked={checked} mr={3} />
              {toDisplayText(option.name, option.id)}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  </FormControl>
);

export const BalanceSheetFilters = ({
  branches,
  costCenters,
  currentFilters,
  loadFilterOptions,
  onApplyFilters,
  onCurrencyChange,
  projects,
}) => {
  const { texts } = useLang();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const defaultDate = getEndOfMonth(new Date().toISOString().split("T")[0]);

  const [selectedDate, setSelectedDate] = useState(
    (currentFilters.date || defaultDate).slice(0, 7),
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState(
    currentFilters.group_by || "",
  );
  const [periodCount, setPeriodCount] = useState(
    currentFilters.period_count || 0,
  );
  const [selectedBranches, setSelectedBranches] = useState(
    currentFilters.branches || [],
  );
  const [selectedProjects, setSelectedProjects] = useState(
    currentFilters.projects || [],
  );
  const [selectedCostCenters, setSelectedCostCenters] = useState(
    currentFilters.costCenters || [],
  );

  useEffect(() => {
    setSelectedDate((currentFilters.date || defaultDate).slice(0, 7));
    setSelectedGroupBy(currentFilters.group_by || "");
    setPeriodCount(currentFilters.period_count || 0);
    setSelectedBranches(currentFilters.branches || []);
    setSelectedProjects(currentFilters.projects || []);
    setSelectedCostCenters(currentFilters.costCenters || []);
  }, [
    currentFilters.branches,
    currentFilters.costCenters,
    currentFilters.date,
    currentFilters.group_by,
    currentFilters.period_count,
    currentFilters.projects,
    defaultDate,
  ]);

  const apply = () => {
    onApplyFilters({
      date: getEndOfMonth(selectedDate),
      period_count: periodCount,
      group_by: selectedGroupBy,
      branches: selectedBranches,
      costCenters: selectedCostCenters,
      projects: selectedProjects,
      contacts: [],
      currency: currentFilters.currency,
    });
    onClose();
  };

  const clear = () => {
    const clearedDate = getEndOfMonth(new Date().toISOString().split("T")[0]);
    setSelectedDate(clearedDate.slice(0, 7));
    setSelectedGroupBy("");
    setPeriodCount(0);
    setSelectedBranches([]);
    setSelectedProjects([]);
    setSelectedCostCenters([]);

    onApplyFilters({
      date: clearedDate,
      period_count: 0,
      group_by: "",
      branches: [],
      costCenters: [],
      projects: [],
      contacts: [],
      currency: currentFilters.currency,
    });
    onClose();
  };

  return (
    <HStack spacing={2}>
      <Menu>
        <ReportMenuButton>
          <HStack spacing={2}>
            <Text fontSize="sm">
              {texts.reports?.figuresIn || "Figures in"}{" "}
              {
                (
                  CURRENCIES.find(
                    (currency) => currency.code === currentFilters.currency,
                  ) || CURRENCIES[0]
                ).label
              }
            </Text>
            <Icon as={FaChevronDown} />
          </HStack>
        </ReportMenuButton>
        <MenuList {...reportToolbarStyles.menuList}>
          {CURRENCIES.map((currency) => (
            <MenuItem
              key={currency.code}
              onClick={() => onCurrencyChange(currency.code)}
            >
              {currency.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      <Popover
        isLazy
        isOpen={isOpen}
        placement="bottom-start"
        onOpen={() => {
          onOpen();
          loadFilterOptions();
        }}
        onClose={onClose}
      >
        <PopoverTrigger>
          <ReportFilterButton>
            {texts.reports?.filter || "Filter"}
          </ReportFilterButton>
        </PopoverTrigger>
        <PopoverContent {...reportToolbarStyles.popoverContent} w="360px">
          <PopoverArrow />
          <PopoverHeader fontWeight="bold">
            {texts.reports?.filter || "Filter"}
          </PopoverHeader>
          <PopoverCloseButton />
          <PopoverBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">
                  {texts.reports?.asAtEndOf || "As at end of (Month/Year)"}
                </FormLabel>
                <Input
                  type="month"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  size="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">
                  {texts.reports?.comparePeriod || "Compare Period"}
                </FormLabel>
                <Select
                  value={selectedGroupBy}
                  onChange={(e) => setSelectedGroupBy(e.target.value)}
                  size="sm"
                >
                  <option value="">{texts.reports?.select || "Select"}</option>
                  <option value="month">
                    {texts.reports?.month || "Month"}
                  </option>
                  <option value="year">{texts.reports?.year || "Year"}</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">
                  {texts.reports?.periods || "Periods"}
                </FormLabel>
                <Select
                  value={periodCount}
                  onChange={(e) => setPeriodCount(parseInt(e.target.value, 10))}
                  size="sm"
                >
                  {PERIOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <MultiSelectMenu
                label={texts.reports?.branch || "Branch"}
                options={branches}
                selectedValues={selectedBranches}
                onSelectionChange={setSelectedBranches}
              />
              <MultiSelectMenu
                label={texts.reports?.project || "Project"}
                options={projects}
                selectedValues={selectedProjects}
                onSelectionChange={setSelectedProjects}
              />
              <MultiSelectMenu
                label={texts.reports?.costCenter || "Cost Center"}
                options={costCenters}
                selectedValues={selectedCostCenters}
                onSelectionChange={setSelectedCostCenters}
              />
              <HStack justify="end">
                <ReportActionButton variant="ghost" onClick={clear}>
                  {texts.reports?.clear || "Clear"}
                </ReportActionButton>
                <ReportActionButton variant="primary" onClick={apply}>
                  {texts.reports?.apply || "Apply"}
                </ReportActionButton>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HStack>
  );
};
